import { apiErrorField } from '@agos/api-client'
import {
  getGetSettingsQrPaymentQueryKey,
  useGetSettingsQrPayment,
  usePatchSettingsQrPayment,
} from '@agos/api-client/payments'
import {
  MAX_NAME_LENGTH,
  MAX_PAYLOAD_BYTES,
  T,
  buildEpcPayload,
  canRender,
  codeModeOf,
  expectedCheckDigits,
  formOf,
  formatIban,
  isReallyActive,
  payloadBytes,
  payloadProblems,
  qrPayload,
  qrProblems,
  referenceFor,
  tt,
  useAuth,
  type QrCodeMode,
  type QrForm,
  type QrProblem,
  type QrSettings,
} from '@agos/shared'
import {
  PageHeader,
  ReadFailedBlock,
  Spinner,
  cn,
  useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle,
  CreditCard,
  Info,
  QrCode,
  Save,
  XCircle,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useMemo, useState, type ReactNode } from 'react'

import { qrSettingsNote, storesQrSettings } from './qr-rights'

/**
 * `/qr-payment` - the bank details a customer's phone scans to pay an order.
 *
 * The endpoint is `PATCH /settings/qr-payment`, not the `PUT` legacy's `fetch` sends -
 * the AGOS controller settled on PATCH for a partial settings update.
 *
 * **FB-086.** The stored payee IBAN is `SK1400000000001285211111` and it **fails mod-97**:
 * its check digits are `14` where the account number requires `55`. Every QR code this
 * page has produced points at an account no bank will accept - and legacy shows a green
 * *QR platba je aktívna* banner over it, because its readiness test is
 * `isActive && iban && accountName` with no regard for whether the IBAN is real.
 *
 * The payload itself is EPC069-12 and legacy builds it correctly field for field. What it
 * never checks is the standard's constraints: the checksum, the BIC's shape, the field
 * lengths, and the 331-**byte** cap that Slovak text reaches sooner than it looks.
 *
 * Ported from legacy `pages/qr-payment-settings.tsx`.
 */

export function QrPaymentSettingsBody() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  // §9.758, BACKEND.md §10A.413 - the save stays the main administrator's (`qr-rights.ts`).
  const { user } = useAuth()
  const mayStore = storesQrSettings(user?.systemRole)
  const { data, isLoading, isError, refetch } = useGetSettingsQrPayment()
  const settings = data as QrSettings | undefined

  const [form, setForm] = useState<QrForm>(() => formOf(undefined))
  const [loadedFrom, setLoadedFrom] = useState<QrSettings | undefined>(undefined)
  const [previewAmount, setPreviewAmount] = useState('10.00')

  if (settings && settings !== loadedFrom) {
    setLoadedFrom(settings)
    setForm(formOf(settings))
  }

  const set = <K extends keyof QrForm>(key: K, value: QrForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  // Judged on what is in the form, not on what is saved - a fix should stop the
  // complaint as it is typed.
  const asSettings: QrSettings = useMemo(
    () => ({
      isActive: form.isActive,
      iban: form.iban,
      swift: form.swift,
      accountName: form.accountName,
      bankName: form.bankName,
    }),
    [form],
  )

  const reference = referenceFor('TEST-001')
  const problems = payloadProblems(asSettings, previewAmount, reference)
  const configProblems = qrProblems(asSettings)
  /**
   * §9.659, FB-988 - which of those problems the chosen mode actually has.
   *
   * `qrProblems` describes the **EPC payload**, which is what `bank` mode encodes. In `gateway` mode the code is
   * the TatraPay session's own URL and the account fields are not read at all, so reporting a missing IBAN in red
   * there is a blocking error about a field nothing will look at - and it is what a franchise on gateway mode was
   * shown while their driver's button was withheld for the same reason (§10A.333).
   *
   * **Stated rather than hidden**: an operator who filled the IBAN in and then switched mode should still see that
   * it is malformed, because switching back is one click. So the account problems move to muted text with one
   * sentence naming what this mode does need, and a BIC problem travels with them.
   */
  const accountMattersHere = form.codeMode !== 'gateway'
  const blockingProblems = accountMattersHere ? configProblems : []
  const asideProblems = accountMattersHere ? [] : configProblems
  const payload = buildEpcPayload(asSettings, previewAmount, reference)
  const showable = canRender(problems)

  const save = usePatchSettingsQrPayment({
    mutation: {
      onSuccess: () => {
        set('tbClientSecret', '')
        void queryClient.invalidateQueries({ queryKey: getGetSettingsQrPaymentQueryKey() })
        toast({ title: tt('Nastavenia uložené') })
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Nastavenia sa nepodarilo uložiť.'),
          variant: 'destructive',
        }),
    },
  })

  /* §9.616 - the editor is declined rather than the save disabled, which is §9.531's decision. On a
   * failed read the form stays `formOf(undefined)`, so every box is blank and `isActive` is false -
   * and `qrPayload` sends all five text fields unconditionally, `isValidIban('')` answers **true** by
   * design, and `COALESCE(:iban, iban)` guards null rather than the empty string. So one press of
   * *Uložiť* wiped the IBAN, SWIFT, account name, bank name and Tatra client id and switched QR
   * payment off - on a row the owner has all four of set. And `qrProblems` reads the **form**, so the
   * blank one reported *Bez IBAN-u nie je kam platiť*: the screen diagnosed the problem the button
   * below it would create.
   *
   * **On `isError`, never on a blank payload.** `settings()` inserts a row with `DEFAULT VALUES` when
   * a franchise has none, so blank fields are the ordinary state for one that has filled nothing in
   * and it has to be able to type its IBAN. `isError` is the only thing that tells *we could not read
   * it* from *nothing is set yet*. */
  if (isError) {
    return (
      <div className="space-y-5">
        <ReadFailedBlock
          marker="read"
          of="qr-payment"
          title={tt('Nastavenia QR platby sa nepodarilo načítať')}
          onRetry={() => void refetch()}
        >
          <T>
            Preto tu nie je ani formulár - prázdne polia by sa dali uložiť a prepísať IBAN, na ktorý
            vám zákazníci platia.
          </T>
        </ReadFailedBlock>
      </div>
    )
  }

  if (isLoading) {
    /* §9.615 - no `PageHeader` here, and that is the fix rather than an omission. Both callers draw
     * one: `QrPaymentSettingsPage` above this body, and `DeliveryHubPage`, whose own docstring is
     * *"Bodies, not pages. Each tab draws the content without its own `PageHeader`"*. A second one
     * here gave the standalone route two headings reading *QR platba* for as long as the read was in
     * flight, which is the strict-mode violation §9.608 measured at 16 failures and read as a race. */
    return (
      <div className="space-y-5" data-qr-settings-loading>
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      </div>
    )
  }

  const reallyActive = isReallyActive(asSettings)

  return (
    <div className="space-y-5">
      {!mayStore && (
        <p className="text-xs text-amber-500" data-qr-main-admin-only>
          {qrSettingsNote()}
        </p>
      )}
      {/* §9.758 - the switch and every bank field in one disabled set for anybody but the main administrator; the
          preview below stays usable, because reading what a code would say is not a write. */}
      <fieldset disabled={!mayStore} className="space-y-5 min-w-0 border-0 p-0 m-0" data-qr-settings-fields>
      {/* §9.302 - the switch and the banner are one subject and were two cards. The banner is
        * FB-086's: legacy called it active on `isActive && iban && accountName`, so a broken IBAN
        * showed green. */}
      <section className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold">
              <T>Aktivovať QR platbu</T>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {/* Legacy renders the literal text `tt("Zaplatiť QR")` here - the call was
                  written inside a JSX text node instead of as an expression. */}
              <T>Na objednávke sa objaví tlačidlo</T>{' '}
              <strong className="text-foreground">{tt('Zaplatiť QR')}</strong>
            </p>
          </div>
          <label className="flex items-center gap-2 shrink-0">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => set('isActive', event.target.checked)}
              aria-label={tt('Aktivovať QR platbu')}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm font-semibold">
              <T>Zapnuté</T>
            </span>
          </label>
        </div>

        {/* §9.302 - the finding behind the redesign. This switch does not save itself: `qrPayload`
          * sends `isActive`, but only when the button in the next card is pressed, and that button
          * used to be labelled *Uložiť bankové údaje* - which names the fields below and not this.
          * Somebody who switched QR payment on and navigated away had turned nothing on. The button
          * now names the whole screen and this says so where the switch is. */}
        {(form.isActive !== (loadedFrom?.isActive === true) ||
          form.codeMode !== codeModeOf(loadedFrom)) && (
          <p
            className="flex items-start gap-1.5 text-xs text-amber-500 border-t border-border pt-3"
            data-qr-unsaved-switch
          >
            <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
            <T>Zmena sa uloží až tlačidlom Uložiť nastavenia QR platby nižšie.</T>
          </p>
        )}

        <div
          data-qr-status={reallyActive ? 'active' : form.isActive ? 'broken' : 'off'}
          className={cn(
            'flex items-start gap-3 rounded-xl px-4 py-3 border text-sm font-medium',
            reallyActive
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
              : form.isActive
                ? 'bg-red-500/10 border-red-500/30 text-red-500'
                : 'bg-muted/50 border-border text-muted-foreground',
          )}
        >
          {reallyActive ? (
            <>
              <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <T>QR platba je aktívna - na objednávkach sa dá vygenerovať QR kód</T>
            </>
          ) : form.isActive ? (
            <>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <T>
                  QR platba je zapnutá, ale bankové údaje nie sú v poriadku - kódy, ktoré sa z nich
                  vygenerujú, sa nedajú zaplatiť.
                </T>
              </span>
            </>
          ) : (
            <>
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <T>QR platba je vypnutá - vyplňte bankové údaje a zapnite ju</T>
            </>
          )}
        </div>
      </section>

      <section className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <h2 className="text-sm font-semibold flex items-center gap-2 pb-1 border-b border-border">
          <CreditCard className="w-4 h-4 text-muted-foreground" />
          <T>Bankové údaje reštaurácie</T>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="IBAN"
            hint={
              form.iban.trim() !== '' && !configProblems.some((problem) => problem.kind.startsWith('iban'))
                ? formatIban(form.iban)
                : undefined
            }
          >
            <input
              value={form.iban}
              onChange={(event) => set('iban', event.target.value)}
              aria-label="IBAN"
              className={cn(INPUT, 'font-mono tracking-wider')}
              placeholder="SK00 0000 0000 0000 0000 0000"
            />
          </Field>

          <Field label="SWIFT / BIC" hint={tt('Voliteľné - EPC verzia 002 ho nevyžaduje')}>
            <input
              value={form.swift}
              onChange={(event) => set('swift', event.target.value)}
              aria-label="SWIFT / BIC"
              className={cn(INPUT, 'font-mono tracking-wider')}
              placeholder="TATRSKBX"
            />
          </Field>

          <Field
            label={tt('Názov účtu')}
            hint={`${form.accountName.trim().length}/${MAX_NAME_LENGTH}`}
          >
            <input
              value={form.accountName}
              onChange={(event) => set('accountName', event.target.value)}
              aria-label={tt('Názov účtu')}
              className={INPUT}
              placeholder="MM PIZZA s.r.o"
            />
          </Field>

          <Field label={tt('Banka')}>
            <input
              value={form.bankName}
              onChange={(event) => set('bankName', event.target.value)}
              aria-label={tt('Banka')}
              className={INPUT}
              placeholder="Tatra Banka"
            />
          </Field>
        </div>

        {/* §9.402 - which of the two codes a driver's sheet draws. Beside the IBAN, because the
          * IBAN is exactly what the choice is about: bank mode encodes it and gateway mode never
          * reads it. Radio buttons rather than a toggle - neither mode is *off*, and a switch
          * labelled with one of them leaves the other unnamed. */}
        <fieldset className="space-y-2 pt-1" data-qr-code-mode-group>
          <legend className="text-xs font-semibold text-muted-foreground pb-1">
            <T>Čo zákazník naskenuje</T>
          </legend>
          {CODE_MODES.map((mode) => (
            <label
              key={mode.value}
              className={cn(
                'flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer transition-colors',
                form.codeMode === mode.value
                  ? 'border-primary/50 bg-primary/5'
                  : 'border-border hover:bg-muted/40',
              )}
            >
              <input
                type="radio"
                name="qr-code-mode"
                value={mode.value}
                checked={form.codeMode === mode.value}
                onChange={() => set('codeMode', mode.value)}
                data-qr-code-mode={mode.value}
                // Named explicitly, so the radio's accessible name is the mode and not the
                // sentence beneath it. Without this the note *"Tento režim IBAN nepoužíva"* put the
                // word IBAN into the label, and `getByLabel('IBAN')` - a substring match - resolved
                // to the IBAN field **and** this radio, which failed eleven specs on this screen.
                aria-label={mode.label()}
                className="mt-0.5"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{mode.label()}</span>
                <span className="block text-[11px] text-muted-foreground leading-relaxed">
                  {mode.note()}
                </span>
              </span>
            </label>
          ))}
          {/* The one thing a franchise with a failing IBAN needs told, and the reason they might
            * pick the gateway: it is the mode that can still take money. §10A.177, FB-086. */}
          {form.codeMode === 'bank' &&
            configProblems.some((problem) => problem.kind.startsWith('iban')) && (
              <p className="text-[11px] text-amber-500 leading-relaxed" data-qr-mode-iban-warning>
                <T>
                  Tento režim potrebuje IBAN, ktorý prejde kontrolou - inak šofér neuvidí žiadny kód.
                  Presmerovanie na platobnú bránu IBAN nepoužíva.
                </T>
              </p>
            )}
        </fieldset>

        {blockingProblems.length > 0 && (
          <div className="space-y-1" data-qr-problems>
            {blockingProblems.map((problem) => (
              <p
                key={problem.kind}
                className="flex items-start gap-1.5 text-xs text-red-500 leading-relaxed"
              >
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                <ProblemText problem={problem} iban={form.iban} />
              </p>
            ))}
          </div>
        )}

        {/* §9.659 - the gateway's own requirement, which is the field this franchise has to check and which
          * nothing on this screen named. `DoorTransferService` opens the session through the card gateway, so
          * `initiate` refuses on the CardPay credentials rather than on anything here (§10A.333). */}
        {!accountMattersHere && (
          <div className="space-y-1" data-qr-gateway-note>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <T>
                Presmerovanie beží cez platobnú bránu, ktorou franšíza prijíma platby kartou - TatraPay+
                alebo GP webpay - a potrebuje prihlasovacie údaje platobnej brány, ktoré nastavuje správca
                platformy (Tower - Integrácie). Údaje účtu nižšie tento režim nepoužíva.
              </T>
            </p>
            {asideProblems.map((problem) => (
              <p
                key={problem.kind}
                className="flex items-start gap-1.5 text-[11px] text-muted-foreground leading-relaxed"
                data-qr-problem-aside={problem.kind}
              >
                <ProblemText problem={problem} iban={form.iban} />
              </p>
            ))}
          </div>
        )}

        {mayStore && (
          <button
            type="button"
            data-qr-save
            onClick={() => save.mutate({ data: qrPayload(form) })}
            disabled={save.isPending}
            className={cn('w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg', BUTTON_PRIMARY)}
          >
            {save.isPending ? <Spinner className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            <T>Uložiť nastavenia QR platby</T>
          </button>
        )}
      </section>
      </fieldset>

      <section className="bg-card border border-border rounded-2xl p-5 space-y-4">
        <h2 className="text-sm font-semibold flex items-center gap-2 pb-1 border-b border-border">
          <QrCode className="w-4 h-4 text-muted-foreground" />
          <T>Náhľad</T>
        </h2>

        <Field label={tt('Suma na ukážku (€)')}>
          <input
            value={previewAmount}
            onChange={(event) => setPreviewAmount(event.target.value)}
            aria-label={tt('Suma na ukážku')}
            className={cn(INPUT, 'max-w-[12rem]')}
            placeholder="10.00"
          />
        </Field>

        {showable ? (
          <div className="flex flex-col sm:flex-row items-start gap-5" data-qr-preview>
            <div className="bg-white p-3 rounded-xl shrink-0">
              <QRCodeSVG value={payload} size={168} level="M" />
            </div>
            <div className="min-w-0 space-y-2">
              <p className="text-[11px] text-muted-foreground tabular-nums" data-payload-bytes>
                {payloadBytes(payload)} / {MAX_PAYLOAD_BYTES} <T>bajtov</T>
              </p>

              {/* §9.302 - this used to sit open beside the code: service tags, a checksum and pipe
                * separators, printed into the page. It is genuinely useful when a bank rejects a
                * code, and it is developer output on a restaurant owner's settings screen. One
                * click away, not the first thing next to the QR square.
                *
                * The byte counter stays out here. It is the one number on this screen somebody acts
                * on - the 331-byte cap is what a long account name breaks - so hiding it behind a
                * disclosure would bury the warning rather than the detail. */}
              <details className="group" data-qr-payload-details>
                <summary className="text-[11px] text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors">
                  <T>Obsah kódu (SEPA EPC)</T>
                </summary>
                <pre
                  className="mt-2 text-[11px] font-mono bg-muted/40 rounded-lg p-3 overflow-x-auto whitespace-pre"
                  data-qr-payload
                >
                  {payload}
                </pre>
              </details>
            </div>
          </div>
        ) : (
          <div
            className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-xs space-y-1"
            data-qr-unrenderable
          >
            <p className="flex items-start gap-1.5 font-semibold text-amber-500">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              <T>Kód sa nedá vygenerovať</T>
            </p>
            {/* Drawing one anyway is worse than drawing none: a customer would scan it. */}
            {problems.map((problem) => (
              <p key={problem.kind} className="text-muted-foreground pl-5">
                <ProblemText problem={problem} iban={form.iban} />
              </p>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

/**
 * `/qr-payment` - the route, which still answers after §9.302 moved this screen into
 * *Nastavenia rozvozu*.
 *
 * §9.208's rule: a hub is not a wall in front of a page somebody has bookmarked. The sidebar entry
 * is folded into the delivery hub, the path is unchanged, and `qr-payment.spec.ts` drives it here.
 * The hub draws `QrPaymentSettingsBody` under its own heading instead.
 */
export function QrPaymentSettingsPage() {
  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('QR platba')}
        // Legacy's own description reads "kompatibilné so všetkými slovenských bankami".
        description={tt('SEPA EPC QR kód - čítajú ho všetky slovenské banky')}
        icon={<QrCode className="w-5 h-5" />}
      />
      <QrPaymentSettingsBody />
    </div>
  )
}

function ProblemText({ problem, iban }: { problem: QrProblem; iban: string }) {
  if (problem.kind === 'iban-checksum') {
    const expected = expectedCheckDigits(iban)
    return (
      <span>
        {/* FB-086 - the stored account is exactly this case. The server refuses the save now too
            (FB-186), so this says what will happen rather than only what is wrong. */}
        <T>IBAN nemá platnú kontrolnú číslicu, takže platba naň neprejde - server ho neuloží.</T>
        {expected !== null && (
          <>
            {' '}
            <T>Podľa čísla účtu by mal začínať</T>{' '}
            <strong>
              {iban.replace(/\s/g, '').slice(0, 2).toUpperCase()}
              {expected}
            </strong>
            .
          </>
        )}
      </span>
    )
  }
  if (problem.kind === 'no-iban') return <T>Bez IBAN-u nie je kam platiť.</T>
  if (problem.kind === 'no-name') return <T>Názov účtu je povinný - vidí ho platiaci zákazník.</T>
  if (problem.kind === 'bic-shape') {
    return <T>SWIFT/BIC má mať 8 alebo 11 znakov.</T>
  }
  if (problem.kind === 'name-too-long') {
    return (
      <span>
        <T>Názov účtu má najviac 70 znakov, tento má</T> {problem.length}.
      </span>
    )
  }
  if (problem.kind === 'payload-too-long') {
    return (
      <span>
        {/* Bytes, not characters - a Slovak diacritic costs two. */}
        <T>Obsah kódu má</T> {problem.bytes} <T>bajtov, povolených je</T> {MAX_PAYLOAD_BYTES}.{' '}
        <T>Skráťte názov účtu alebo poznámku - diakritika sa počíta za dva bajty.</T>
      </span>
    )
  }
  return <T>Suma musí byť väčšia ako nula.</T>
}

/**
 * The two codes, each with the sentence that decides it - §9.402.
 *
 * **Labels are functions, not strings.** A module-level `tt('...')` is evaluated at import, which
 * pins the language before the switcher runs; calling at render is what every other screen here
 * does. `value` matches the wire vocabulary exactly, so the radio's own value is what gets saved.
 */
const CODE_MODES: ReadonlyArray<{ value: QrCodeMode; label: () => string; note: () => string }> = [
  {
    value: 'bank',
    label: () => tt('Prevod v bankovej aplikácii'),
    note: () =>
      tt(
        'Zákazník naskenuje kód a jeho banka predvyplní príjemcu, sumu a poznámku. Kód prečíta každá banková aplikácia.',
      ),
  },
  {
    value: 'gateway',
    // §9.795 - the gateway the franchise takes cards through (BACKEND.md §10A.446), not TatraPay by name.
    label: () => tt('Presmerovanie na platobnú bránu'),
    note: () =>
      tt(
        'Kód otvorí platobnú stránku brány, ktorou franšíza prijíma platby kartou - TatraPay+ alebo GP webpay - a zákazník tam zaplatí kartou, Google Pay alebo Apple Pay. Tento režim IBAN nepoužíva.',
      ),
  },
]

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground/80 font-mono">{hint}</p>}
    </div>
  )
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
